import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import Navigation from './Navigation';
import Footer from './Footer';
import config from '../config/api';
import turnstileConfig from '../config/turnstile';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load Turnstile
  useEffect(() => {
    const loadTurnstile = () => {
      if (!document.getElementById('turnstile-script')) {
        const script = document.createElement('script');
        script.id = 'turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        document.head.appendChild(script);
        script.onload = renderTurnstile;
      } else if (window.turnstile) {
        renderTurnstile();
      }
    };
    const renderTurnstile = () => {
      if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileConfig.siteKey,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'dark',
          language: 'vi'
        });
      }
    };
    loadTurnstile();
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (formData.service && formData.service !== '') {
        const serviceTypeMap = {
          'support': 'SUPPORT',
          'Thiết kế Website': 'FEATURE',
          'Ứng dụng Mobile': 'FEATURE',
          'E-commerce': 'FEATURE',
          'Digital Marketing': 'OTHER',
          'Hosting & Domain': 'SUPPORT',
          'Tư vấn IT': 'SUPPORT',
          'Khác': 'OTHER'
        };

        const ticketData = {
          title: `${formData.service} từ ${formData.name} - ${formData.company || 'Khách hàng'}`,
          description: `Thông tin liên hệ:\n- Tên: ${formData.name}\n- Email: ${formData.email}\n- Điện thoại: ${formData.phone}\n- Công ty: ${formData.company || 'Không có'}\n- Dịch vụ: ${formData.service}\n- Ngân sách: ${formData.budget}\n\nNội dung:\n${formData.message}`,
          guest_name: formData.name,
          guest_email: formData.email,
          priority: 'MEDIUM',
          type: serviceTypeMap[formData.service] || 'OTHER'
        };

        const endpoints = [
          config.FALLBACK_SUBMIT_URL,
          config.SUBMIT_TICKET_URL
        ];

        let success = false;

        for (const endpoint of endpoints) {
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(ticketData)
            });

            if (response.ok) {
              success = true;
              break;
            }
          } catch (fetchError) {
            console.error(`Error ${endpoint}:`, fetchError);
          }
        }

        if (!success) {
          throw new Error('All endpoints failed');
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      setSubmitStatus('success');
      setFormData({
        name: '', email: '', phone: '', company: '', service: '', budget: '', message: ''
      });
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <PhoneIcon className="w-6 h-6" />,
      title: "Điện thoại",
      details: ["0903954852"],
      description: "Gọi ngay để được tư vấn miễn phí"
    },
    {
      icon: <EnvelopeIcon className="w-6 h-6" />,
      title: "Email",
      details: ["support@giaiphapthietkeweb.com", "voduythien16392@gmail.com"],
      description: "Chúng tôi phản hồi trong vòng 24h"
    },
    {
      icon: <MapPinIcon className="w-6 h-6" />,
      title: "Trụ sở chính",
      details: ["22/2 Đường số 4, Phường Bình Hưng Hòa", "Quận Bình Tân, TP.HCM"],
      description: "Văn phòng chính tại TP.HCM"
    },
    {
      icon: <ClockIcon className="w-6 h-6" />,
      title: "Giờ làm việc",
      details: ["T2-T6: 9:00 - 18:00", "T7: 9:00 - 12:00"],
      description: "Giờ địa phương (GMT+7)"
    }
  ];

  const services = [
    "Thiết kế Website",
    "Ứng dụng Mobile",
    "E-commerce",
    "Digital Marketing",
    "Hosting & Domain",
    "Tư vấn IT",
    "support",
    "Khác"
  ];

  const budgetRanges = [
    "Dưới 10 triệu VND",
    "10 - 20 triệu VND",
    "20 - 50 triệu VND",
    "50 triệu - 500 triệu VND",
    "Trên 1 tỷ VND",
    "Cần tư vấn"
  ];

  return (
    <div className="min-h-screen bg-dark-900">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-16 bg-gradient-to-br from-dark-900 via-dark-800 to-ocean-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Liên hệ với <span className="text-white">Smart</span><span className="text-cyan-400">era</span>
            </h1>
            <p className="text-xl text-ocean-100/70 max-w-3xl mx-auto">
              Sẵn sàng bắt đầu dự án của bạn? Hãy liên hệ với chúng tôi để được tư vấn miễn phí
              và nhận báo giá phù hợp nhất.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center">
                <div className="bg-cyan-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                  <div className="text-cyan-400">
                    {info.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{info.title}</h3>
                <div className="space-y-1 mb-2">
                  {info.details.map((detail, idx) => (
                    <div key={idx} className="text-white font-medium">{detail}</div>
                  ))}
                </div>
                <p className="text-ocean-100/60 text-sm">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-dark-800/50 backdrop-blur-xl p-8 rounded-2xl border border-cyan-500/20">
              <h2 className="text-3xl font-bold text-white mb-6">Gửi tin nhắn</h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-400 mr-2" />
                  <span className="text-emerald-400">Cảm ơn bạn! Chúng tôi sẽ liên hệ lại trong thời gian sớm nhất.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
                  <span className="text-red-400">Có lỗi xảy ra. Vui lòng thử lại sau.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ocean-100/80 mb-2">Họ và tên *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                      placeholder="Nguyễn Văn A"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ocean-100/80 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ocean-100/80 mb-2">Số điện thoại</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                      placeholder="+84 123 456 789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ocean-100/80 mb-2">Công ty</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                      placeholder="Tên công ty"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ocean-100/80 mb-2">Dịch vụ quan tâm</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                    >
                      <option value="">Chọn dịch vụ</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service === 'support' ? 'Hỗ trợ kỹ thuật' : service}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ocean-100/80 mb-2">Ngân sách dự kiến</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
                    >
                      <option value="">Chọn ngân sách</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ocean-100/80 mb-2">Mô tả dự án *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all resize-none"
                    placeholder="Hãy mô tả chi tiết về dự án mà bạn muốn thực hiện..."
                  />
                </div>

                {/* Turnstile Widget */}
                <div className="flex justify-center my-4">
                  <div ref={turnstileRef}></div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !turnstileToken}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-3 rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all font-semibold inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/25"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Đang gửi...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                      Gửi tin nhắn
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Map & Additional Info */}
            <div className="space-y-8">
              <div className="bg-dark-800/50 backdrop-blur-xl p-8 rounded-2xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-white mb-6">Vị trí văn phòng</h3>

                <div className="space-y-6">
                  <div className="border-l-4 border-cyan-400 pl-4">
                    <h4 className="font-bold text-white mb-2">Trụ sở chính - TP.HCM</h4>
                    <p className="text-ocean-100/60 text-sm mb-1">22/2 Đường số 4, Phường Bình Hưng Hòa</p>
                    <p className="text-ocean-100/60 text-sm mb-3">Quận Bình Tân, TP.HCM</p>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center text-cyan-400">
                        <PhoneIcon className="w-4 h-4 mr-1" />
                        0903954852
                      </div>
                      <div className="flex items-center text-cyan-400">
                        <EnvelopeIcon className="w-4 h-4 mr-1" />
                        support@giaiphapthietkeweb.com
                      </div>
                    </div>
                  </div>

                  <div className="border-l-4 border-emerald-400 pl-4">
                    <h4 className="font-bold text-white mb-2">Chi nhánh - Đà Nẵng</h4>
                    <p className="text-ocean-100/60 text-sm mb-1">Lô 1 – Lô 2 Khu A4, Khu phức hợp đô thị thương mại cao tầng Phương Trang</p>
                    <p className="text-ocean-100/60 text-sm mb-1">Đường Nguyễn Sinh Sắc, Phường Hoà Minh</p>
                    <p className="text-ocean-100/60 text-sm mb-3">Quận Liên Chiểu, TP. Đà Nẵng</p>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center text-emerald-400">
                        <PhoneIcon className="w-4 h-4 mr-1" />
                        0903954852
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-cyan-500/10">
                  <h4 className="font-bold text-white mb-3">Liên kết hữu ích</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <a href="https://smartera.uk" target="_blank" rel="noopener noreferrer" className="flex items-center text-cyan-400 hover:text-cyan-300 text-sm">
                      <GlobeAltIcon className="w-4 h-4 mr-2" />
                      Website: smartera.uk
                    </a>
                    <a href="https://giaiphapthietkeweb.com" target="_blank" rel="noopener noreferrer" className="flex items-center text-cyan-400 hover:text-cyan-300 text-sm">
                      <GlobeAltIcon className="w-4 h-4 mr-2" />
                      Blog: giaiphapthietkeweb.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-dark-800/50 backdrop-blur-xl p-8 rounded-2xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-white mb-6">Câu hỏi thường gặp</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-white mb-2">Thời gian hoàn thành dự án?</h4>
                    <p className="text-ocean-100/60 text-sm">Tùy thuộc vào quy mô và độ phức tạp, thời gian từ 2-12 tuần cho hầu hết các dự án.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">Chi phí dự án được tính như thế nào?</h4>
                    <p className="text-ocean-100/60 text-sm">Chúng tôi báo giá dựa trên yêu cầu cụ thể và cung cấp báo giá miễn phí chi tiết.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">Có hỗ trợ sau bàn giao không?</h4>
                    <p className="text-ocean-100/60 text-sm">Có, chúng tôi cung cấp bảo hành và hỗ trợ kỹ thuật sau bàn giao sản phẩm.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
